import { useCallback, useEffect, useRef, useState } from "react";

import {
	collapseSelectionAfterPlaceholder,
	EditorLike,
	getEditorBookmark,
} from "@/pages/Private/pages/Messages/components/AiSnippets/hooks/useEditorCursorInsertion";

/** Raw TinyMCE surface the footer wiring relies on. */
export type FooterEditor = EditorLike & {
	getContent?: () => string;
	setContent?: (content: string) => void;
	plugins?: { wordcount?: { body?: { getCharacterCount?: () => number } } };
};

const COUNT_THROTTLE_MS = 150;

/**
 * Editor-side wiring of the footer bar, scoped to ONE TinyMCE instance (two
 * editors can be mounted at once on invite nodes, so the global last-focused
 * registry must not be used here). Call `wireEditorForFooter` from the
 * editor's `setup` callback; it captures the instance, snapshots the cursor
 * bookmark on blur and keeps a throttled live character count.
 */
export const useEditorFooterWiring = () => {
	const editorRef = useRef<FooterEditor | null>(null);
	const bookmarkRef = useRef<unknown>(null);
	const countTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [charCount, setCharCount] = useState<number>(0);

	useEffect(() => {
		return () => {
			if (countTimerRef.current) {
				clearTimeout(countTimerRef.current);
			}
		};
	}, []);

	const readCharCount = useCallback(() => {
		setCharCount(editorRef.current?.plugins?.wordcount?.body?.getCharacterCount?.() ?? 0);
	}, []);

	const scheduleCharCount = useCallback(() => {
		if (countTimerRef.current) {
			return;
		}

		countTimerRef.current = setTimeout(() => {
			countTimerRef.current = null;
			readCharCount();
		}, COUNT_THROTTLE_MS);
	}, [readCharCount]);

	const wireEditorForFooter = useCallback(
		(editor: FooterEditor) => {
			editorRef.current = editor;

			editor.on?.("blur", () => {
				const bookmark = getEditorBookmark(editor);

				if (bookmark) {
					bookmarkRef.current = bookmark;
				}
			});

			// SetContent covers template replacement and external rewrites
			// (e.g. spintax/AI snippet renames); init seeds the initial count.
			editor.on?.("keyup input SetContent init", scheduleCharCount);

			editor.on?.("remove", () => {
				if (editorRef.current === editor) {
					editorRef.current = null;
					bookmarkRef.current = null;
				}
			});
		},
		[scheduleCharCount]
	);

	/** Refreshes the stored bookmark while the editor still has focus. */
	const captureCursor = useCallback(() => {
		const editor = editorRef.current;

		if (!editor?.hasFocus?.()) {
			return;
		}

		const bookmark = getEditorBookmark(editor);

		if (bookmark) {
			bookmarkRef.current = bookmark;
		}
	}, []);

	/**
	 * Inserts a placeholder token at the last cursor position of this editor.
	 * Falls back to the end of the content when the editor was never focused.
	 */
	const insertToken = useCallback((token: string): boolean => {
		const editor = editorRef.current;

		if (!editor) {
			return false;
		}

		try {
			editor.focus?.();

			if (bookmarkRef.current) {
				editor.selection?.moveToBookmark?.(bookmarkRef.current);
			} else {
				const body = editor.getBody?.();

				if (body) {
					editor.selection?.select?.(body, true);
					editor.selection?.collapse?.(false);
				}
			}

			collapseSelectionAfterPlaceholder(editor);

			editor.insertContent?.(token);
			editor.fire?.("change");

			// Refresh the bookmark to the position after insertion so
			// subsequent inserts continue in the right place.
			const updated = getEditorBookmark(editor);

			if (updated) {
				bookmarkRef.current = updated;
			}

			return true;
		} catch {
			return false;
		}
	}, []);

	const getEditor = useCallback((): FooterEditor | null => editorRef.current, []);

	return { wireEditorForFooter, charCount, insertToken, captureCursor, getEditor };
};
